import { isDevelopmentEnvironment } from "../../estada-config";
import Cabinet from "../../models/Cabinet";
import { initCableTypes } from "../../models/CableType";
import { renumberAllphysicalAddresses } from "../../models/Device";
import Floor from "../../models/Floor";
import { mmToPixel } from "../../models/Page";
import type Page from "../../models/Page";
import Point from "../../models/Point";
import ProjectMetadata from "../../models/ProjectMetadata";
import Room from "../../models/Room";
import { EventKey } from "../../eventLogger/EventKeys";
import Scene from "../../models/Scene";
import { createMissingTemplateGroups } from "../../models/TemplateGroup";
export { createMissingTemplateGroups } from "../../models/TemplateGroup";
import { suggestEquipmentForRooms } from "./equipmentSuggestion";
import type { EquipmentCounts } from "./equipmentTypes";
import { saveProject } from "../SaveProject";
import ProjectLicense from "../../models/ProjectLicense";

export type DetectedRoom = { name: string; typ: string; flaeche_m2: number | null };
// pdfPageNumber: die 1-basierte Seite im hochgeladenen PDF, auf der diese Etage erkannt wurde -
// wird verwendet, um diese Seite automatisch als Grundriss/Hintergrundbild der Etage zu setzen.
// Zeigt eine PDF-Seite mehrere Etagen, taucht dieselbe pdfPageNumber in mehreren DetectedFloor-
// Einträgen auf.
export type DetectedFloor = { name: string; pdfPageNumber: number; raeume: DetectedRoom[] };

// wird true, sobald die erste KI-Erkennung angewendet wurde – danach werden
// weitere Uploads (z.B. separate PDFs je Etage) ergänzt statt die vorherige
// Erkennung wieder zu überschreiben.
let aiFloorsApplied = false;

// Raumtyp/Fläche aus der KI-Erkennung, je Raum-Id – wird für die automatischen
// Ausstattungs-Annahmen (siehe equipmentAssumptions.ts) benötigt. Existiert für
// einen Raum kein Eintrag, wurde er nicht per KI erkannt (z.B. manuell hinzugefügt).
export type RoomAiInfo = { typ: string; flaecheM2: number | null; isBasementFloor: boolean };
const roomAiInfoById = new Map<string, RoomAiInfo>();

export function getRoomAiInfo(roomId: string): RoomAiInfo | undefined {
  return roomAiInfoById.get(roomId);
}

// Von Claude vorgeschlagene Ausstattung je per KI erkanntem Raum (siehe
// equipmentSuggestion.ts) - wird einmalig pro Grundriss-Upload befüllt, direkt nachdem alle
// Räume aus applyDetectedFloors() angelegt wurden. Existiert für einen Raum kein Eintrag
// (z.B. weil der Claude-Aufruf fehlgeschlagen ist), fällt equipmentAssumptions.ts auf die
// lokale Heuristik zurück.
const roomEquipmentSuggestionById = new Map<string, EquipmentCounts>();

export function getRoomEquipmentSuggestion(roomId: string): EquipmentCounts | undefined {
  return roomEquipmentSuggestionById.get(roomId);
}

// Fest vorgegebene Ausstattung je Raum eines Beispiel-Projekts (siehe exampleProjects/
// applyExampleProject.ts) - wird direkt beim Anlegen der Räume befüllt, noch BEVOR die
// eigentlichen Consumer/Buttons erzeugt werden (das passiert dort bewusst erst später, siehe
// createPendingExampleConsumers). Ohne diese Zwischenspeicherung würde die Räume-Anzeige des
// Assistenten (die schon vorher die Stückzahlen je Ausstattungsart braucht) für diese Räume
// fälschlich 0 anzeigen.
const roomExampleEquipmentCountsById = new Map<string, EquipmentCounts>();

export function setRoomExampleEquipmentCounts(roomId: string, counts: EquipmentCounts) {
  roomExampleEquipmentCountsById.set(roomId, counts);
}

export function getRoomExampleEquipmentCounts(roomId: string): EquipmentCounts | undefined {
  return roomExampleEquipmentCountsById.get(roomId);
}

// Ursprünglicher Raum-Name-Key ("step2.xxx") je Standard-Raum, der ohne KI-Erkennung über
// createRoomsWithoutUpdate angelegt wurde – der Room.name ist zu diesem Zeitpunkt bereits
// übersetzt, deshalb merken wir uns den Key separat für die Default-Ausstattung
// (siehe defaultRoomEquipment.ts).
const roomDefaultKeyById = new Map<string, string>();

export function getRoomDefaultKey(roomId: string): string | undefined {
  return roomDefaultKeyById.get(roomId);
}

const BASEMENT_FLOOR_NAME = /keller|untergeschoss|^ug$|^kg$/i;

export async function applyDetectedFloors(detectedFloors: DetectedFloor[], pdfDocument?: Page) {
  if (detectedFloors.length === 0) {
    return;
  }
  if (!aiFloorsApplied) {
    aiFloorsApplied = true;
    state.floors.asArray().forEach((floor) => state.floors.remove(floor));
  }
  const newlyCreatedRooms: { roomId: string; name: string; flaecheM2: number | null }[] = [];
  for (const detected of detectedFloors) {
    const newFloor = new Floor({ name: detected.name }, state.floors);
    newFloor.id = newFloor._id + "_detected"; // damit wir die Etagen aus der KI-Erkennung von den manuell angelegten Etagen unterscheiden können
    newFloor.generateTagFromName();
    state.floors.add(newFloor);
    state.eventLogger.addEvent(EventKey.QuickstartItemChanged, {
      step: 1,
      action: "added", // no itx
      itemType: "Floor",
      itemName: detected.name,
    });
    // Die PDF-Seite, auf der diese Etage erkannt wurde, direkt als Grundriss/Hintergrundbild
    // übernehmen - erspart die frühere manuelle Zuordnung je Seite. Zeigt eine Seite mehrere
    // Etagen, erhält hier jede von ihnen unabhängig dieselbe pdfDocument/pageNumber-Zuordnung.
    if (pdfDocument) {
      await newFloor.getOrCreatePage().setBackgroundImageFromBuildingPdf(pdfDocument, detected.pdfPageNumber);
    }
    const isBasementFloor = BASEMENT_FLOOR_NAME.test(detected.name.trim());
    for (const room of detected.raeume) {
      const newRoom = await addRoom(newFloor, room.name);
      roomAiInfoById.set(newRoom.id, {
        typ: room.typ,
        flaecheM2: room.flaeche_m2,
        isBasementFloor,
      });
      newlyCreatedRooms.push({ roomId: newRoom.id, name: room.name, flaecheM2: room.flaeche_m2 });
    }
  }

  // Ausstattungs-Vorschläge für alle neu angelegten Räume in einem gebündelten Aufruf
  // einholen (statt einem Aufruf je Raum) - siehe equipmentSuggestion.ts.
  const suggestions = await suggestEquipmentForRooms(newlyCreatedRooms);
  suggestions.forEach((equipment, roomId) => {
    roomEquipmentSuggestionById.set(roomId, equipment);
  });
}

export async function addRoom(floor: Floor, name: string, point?: Point): Promise<Room> {
  const newRoom = new Room({ name: name }, floor);
  newRoom.generateTagFromName();
  if (point) {
    const floorPage = floor.getOrCreatePage();
    await floorPage.createRoomInPage(newRoom, point);
  }
  floor.rooms.addWithoutUpdate(newRoom);
  state.eventLogger.addEvent(EventKey.QuickstartItemChanged, {
    step: 3,
    action: "added", // no itx
    itemType: "Room",
    itemName: name,
  });
  return newRoom;
}

export function createDefaultCabintAndScenes() {
  ///////////////////////////////////////////////////////////////////
  // create a central Cabinet for this house
  // wir erstellen einen zentralen Schlatschrank für das Haus
  ///////////////////////////////////////////////////////////////////

  const cabinet = new Cabinet(
    {
      name: state.translate("defaultCabinetAndScenes.cabinet1", "Schaltshrank"),
    },
    state.cabinets,
  );
  cabinet.tag = state.nameAndTagManager.generateTag(cabinet);
  state.cabinets.addWithoutUpdate(cabinet);
  // const cabinetDecent = new Cabinet(
  //   {
  //     name: state.translate(
  //       "defaultCabinetAndScenes.cabinet2",
  //       "Schaltshrank decentrally",
  //     ),
  //     tag: state.translate("defaultCabinetAndScenes.cabinet2Tag", "SD1"),
  //   },
  //   state.cabinets,
  // );
  // state.cabinets.addWithoutUpdate(cabinetDecent);

  ///////////////////////////////////////////////////////////////////
  // All Floors get the central "house cabinet"
  // alle Etagen erhalten den zentralen "Haus Schaltkasten"
  ///////////////////////////////////////////////////////////////////

  state.floors.forEach((floor) => {
    floor.cabinetId = cabinet.id;
  });

  /////////////////////////////////
  // default scenes
  // Wir legen auch ein paar default Scenen an

  // const state.sceneGroup = new SceneGroup({}, state.sceneGroups);
  // state.sceneGroup.name = `${state.translate("components.plural.Scene", "Scenes")} ${state.translate(
  //   "defaultCabinetAndScenes.hous",
  //   "Haus",
  // )}`;
  // state.sceneGroups.addWithoutUpdate(state.sceneGroup.);

  state.sceneGroup.addWithoutUpdate(
    new Scene(
      {
        name: state.translate("defaultCabinetAndScenes.living", "Wohnen"),
        sceneNumber: 1,
      },
      state.sceneGroup,
    ),
  );

  state.sceneGroup.addWithoutUpdate(
    new Scene(
      {
        name: state.translate("defaultCabinetAndScenes.eating", "Essen"),
        sceneNumber: 2,
      },
      state.sceneGroup,
    ),
  );

  state.sceneGroup.addWithoutUpdate(
    new Scene(
      {
        name: state.translate("defaultCabinetAndScenes.cooking", "Kochen"),
        sceneNumber: 3,
      },
      state.sceneGroup,
    ),
  );

  state.sceneGroup.addWithoutUpdate(
    new Scene(
      {
        name: state.translate("defaultCabinetAndScenes.sleeping", "Schlafen"),
        sceneNumber: 4,
      },
      state.sceneGroup,
    ),
  );

  /////////////////////////////////////////////////////////////////////////////////
  // create template groups
  // Und noch 2 Template Gruppen für Aktoren und Buttonen
  createMissingTemplateGroups();
}

async function createRoomsWithoutUpdate(floor: Floor) {
  // why cabt this be used inside here
  const floorRoomMap = new Map<string, Array<string>>();
  floorRoomMap.set(state.translate("step1.groundFloor", "Erdgeschoss"), [
    "step2.corridor", // no itx
    "step2.guestToilet", // no itx
    "step2.livingRoom", // no itx
    "step2.diningRoom", // no itx
    "step2.kitchen", // no itx
  ]);
  floorRoomMap.set(state.translate("step1.attic", "Dachgeschoss"), [
    "step2.guestRoom", // no itx
  ]);
  floorRoomMap.set(state.translate("step1.upperFloor", "Obergeschoss"), [
    "step2.corridor", // no itx
    "step2.bedroom", // no itx
    "step2.bathroom", // no itx
    "step2.nursery", // no itx
    "step2.office", // no itx
  ]);

  const newRoomNames = floorRoomMap.get(floor.name);
  const floorPage = floor.getOrCreatePage();
  if (newRoomNames) {
    for (let i = 0; i < newRoomNames?.length; i++) {
      const roomname = newRoomNames[i];
      const point = new Point((297 / 2) * mmToPixel, (210 / 2) * mmToPixel);
      const freeFloorPoint = floorPage.findFreePositionForNewRoom(point);

      const newRoom = await addRoom(floor, state.translate(roomname, roomname.substring(roomname.indexOf(".") + 1)));
      roomDefaultKeyById.set(newRoom.id, roomname);
    }
  }
}

export function createProjectData(type: "quickstart" | "onboarding" = "quickstart") {
  state.projectMetadata = new ProjectMetadata();
  state.projectMetadata.setUserForNewProject();
  state.clear();

  // get knxschema via user?
  if (state.loggedInUser?.onboarding_quiz) {
    for (const [key, value] of Object.entries(state.loggedInUser.onboarding_quiz)) {
      if (key.includes("schema") && (value == "a" || value == "b" || value == "c")) {
        state.settings.knxGroupAddressGeneneration = (value as string).toUpperCase() as "A" | "B" | "C";
      }
    }
  }

  state.projectMetadata.name = ``;
  // for license handeling
  state.projectMetadata.projectLicence = {
    invoiceIds: [] as Array<string>,
  };
  // the actual licenses live on state.projectLicense (not on projectMetadata.projectLicence),
  // so they must be reset here too, otherwise the copy inherits the original project's licenses
  state.projectLicense = new ProjectLicense({}, state.root);

  if (type == "quickstart") {
    return;
  }
  const floorNames = [
    state.translate("step1.groundFloor", "Erdgeschoss"),
    state.translate("step1.upperFloor", "Obergeschoss"),
    state.translate("step1.attic", "Dachgeschoss"),
  ];
  floorNames.forEach((name) => {
    const newFloor = new Floor({ name: name }, state.floors);
    newFloor.generateTagFromName();
    state.floors.addWithoutUpdate(newFloor);
    createRoomsWithoutUpdate(newFloor);
  });
  createDefaultCabintAndScenes();
  state.eventLogger.addEvent(EventKey.ProjectCreated, {
    projectId: state.projectMetadata.id,
    creationType: "Assistant",
  });
}

export function goToProject() {
  initCableTypes();
  renumberAllphysicalAddresses();
  if (!isDevelopmentEnvironment()) {
    saveProject(false);
  }

  state.navigate("/app");
}
